import React from 'react';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    LinearProgress,
    Link,
    Switch,
    Typography,
    useTheme
} from '@mui/material';
import { useSettingsContext } from 'contexts/settingsContext';
import * as settingService from 'services/settings-service';
import * as documentService from 'services/document-service';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/authContext';
import * as Styles from 'pages/development/settings/Settings.styles';
import { IconContext } from 'react-icons';
import { AiFillFilePdf } from 'react-icons/ai';
import { IPropsSettings } from '../Settings';
import { useTenantContext } from 'contexts/tenantContext';


const DocumentsTab = (props: IPropsSettings) => {
    const { tenantId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { updateSetting } = useSettingsContext();
    const { currentTenant } = useTenantContext();
    const { checkAuth } = useAuthContext();
    const [useVertrouwelijk, setUseVertrouwelijk] = React.useState<boolean>(false);
    const [newUseVertrouwelijk, setNewUseVertrouwelijk] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        setLoading(true);
        settingService.getSettings(tenantId).then((res) => {
            setUseVertrouwelijk(res.useVertrouwelijk);
            setNewUseVertrouwelijk(res.useVertrouwelijk);
            setLoading(false);
        });
    }, [tenantId]);

    const handleFileDownload = (fileName: 'aanvraag' | 'attest' | 'vertrouwelijk' | 'Privacyverklaring') => {
        if (fileName === 'Privacyverklaring') {
            documentService.getPrivacyDocument(tenantId).then((res: Blob) => {
                if (res !== undefined) {
                    var fileUrl = URL.createObjectURL(res);
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.download = 'Privacyverklaring.pdf';
                    link.click();
                }
            });
        }
        else {
            settingService.downloadFile(fileName, tenantId).then((res: Blob) => {
                if (res !== undefined) {
                    var fileUrl = URL.createObjectURL(res);
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.download = ((): string => {
                        switch (fileName) {
                            case 'aanvraag':
                                return t('settings.documents.downloadRequestName') + '.pdf';
                            case 'attest':
                                return t('settings.documents.downloadCertificateName') + '.pdf';
                            case 'vertrouwelijk':
                                return t('settings.documents.downloadVertrouwelijkName') + '.pdf';
                            default:
                                return '';
                        }
                    })();
                    link.click();
                }
            });
        }
    }

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, enabled: boolean) => {
        setNewUseVertrouwelijk(enabled);
    }

    const handleUpdate = () => {
        settingService.updateVertrouwelijk(newUseVertrouwelijk, tenantId).then((res) => {
            if (tenantId === currentTenant?.id) {
                updateSetting<boolean>('useVertrouwelijk', res.useVertrouwelijk);
            }
            setUseVertrouwelijk(res.useVertrouwelijk);
        });
    }
    
    const handleCancel = () => {
        setNewUseVertrouwelijk(useVertrouwelijk);
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.documents.title')}
            </Typography>
            <Box>
                {t('settings.documents.description')}
            </Box>
            {loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <LinearProgress/>
                </Box>
            )}
            {!loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.documents.request')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Box>
                                <Link
                                    href="#/aanvraag"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFileDownload('aanvraag');
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                        <IconContext.Provider
                                            value={{
                                                size: "17px",
                                                style: { marginRight: "5px", alignSelf: "center" },
                                                color: "red",
                                            }}
                                        >
                                            <Box component={AiFillFilePdf}/>
                                        </IconContext.Provider>
                                        {t('settings.documents.downloadRequestName')}
                                    </Box>
                                </Link>
                                <FormHelperText sx={{margin: "3px 14px 0 14px"}}>{t('settings.documents.requestHelper')}</FormHelperText>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{wordBreak: 'break-word'}}>
                                {t('settings.documents.certificate')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Box>
                                <Link
                                    href="#/attest"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFileDownload('attest');
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                        <IconContext.Provider
                                            value={{
                                                size: "17px",
                                                style: { marginRight: "5px", alignSelf: "center" },
                                                color: "red",
                                            }}
                                        >
                                            <Box component={AiFillFilePdf}/>
                                        </IconContext.Provider>
                                        {t('settings.documents.downloadCertificateName')}
                                    </Box>
                                </Link>
                                <FormHelperText sx={{margin: "3px 14px 0 14px"}}>{t('settings.documents.certificateHelper')}</FormHelperText>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{wordBreak: 'break-word'}}>
                                {t('settings.documents.privacystatement')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Box>
                                <Link
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleFileDownload('Privacyverklaring');
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                        <IconContext.Provider
                                            value={{
                                                size: "17px",
                                                style: { marginRight: "5px", alignSelf: "center" },
                                                color: "red",
                                            }}
                                        >
                                            <Box component={AiFillFilePdf}/>
                                        </IconContext.Provider>
                                        {t('settings.documents.privacystatement')}
                                    </Box>
                                </Link>
                                <FormHelperText sx={{margin: "3px 14px 0 14px"}}>{t('settings.documents.privacystatementHelper')}</FormHelperText>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{wordBreak: 'break-word'}}>
                                {t('settings.documents.vertrouwelijkEnabled')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            disabled={!checkAuth(['Administrator'])}
                                            onChange={handleSwitchChange}
                                            checked={newUseVertrouwelijk}
                                        />
                                    }
                                    label=""
                                    disabled={!checkAuth(['TenantAdministrator'])}
                                />
                                <FormHelperText>
                                    {t('settings.documents.vertrouwelijkHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{wordBreak: 'break-word'}}>
                                {t('settings.documents.vertrouwelijk')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Link
                                href="#/vertrouwelijk"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleFileDownload('vertrouwelijk');
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                    <IconContext.Provider
                                        value={{
                                            size: "17px",
                                            style: { marginRight: "5px", alignSelf: "center" },
                                            color: "red",
                                        }}
                                    >
                                        <Box component={AiFillFilePdf}/>
                                    </IconContext.Provider>
                                    {t('settings.documents.downloadVertrouwelijkName')}
                                </Box>
                            </Link>
                        </Grid>
                    </Grid>

                    {useVertrouwelijk !== newUseVertrouwelijk && (
                        <Grid item xs={12} md={12} sx={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Button onClick={handleCancel} variant='contained' color='cancel' sx={{marginRight: '1rem'}}>
                                {t('buttons.cancel')}
                            </Button>
                            <Button onClick={handleUpdate} variant='contained' color='success' sx={{margingLeft: '1rem'}}>
                                {t('buttons.save')}
                            </Button>
                        </Grid>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default DocumentsTab;